import React, {useMemo} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {DatePicker} from 'components/ui-kit/DatePicker';
import {DateRange} from 'components/ui-kit/DateRange';
import {Select} from 'components/ui-kit/Select';
import {ROUTE} from 'core/routes';
import {billingAccess$} from 'models/profile/billing/access';
import {
    BILLING_TYPES,
    BILLING_PAYMENT_STATUSES,
    BillingHistoryAccessors as Accessors,
    BillingInvoicesAccessors as InvoicesAccessors,
    BILLING_INVOICE_STATUS,
} from 'types/billing';
import {mapArrayToI18nOptions} from 'utils/select';

export const useSetTableConfig = (path: {path: string}) => {
    const {t} = useTranslation();
    const access = useStore(billingAccess$);
    return useMemo(() => {
        if (path.path === ROUTE.BILLING) {
            return [
                {
                    id: Accessors.id,
                    header: t('pages.profile.billing.fields.id'),
                    width: 100,
                    minWidth: 100,
                    maxWidth: 120,
                },
                {
                    id: Accessors.documentNumber,
                    header: t('pages.profile.billing.fields.documentNumber'),
                    width: 360,
                    minWidth: 320,
                },
                {
                    id: Accessors.amount,
                    header: t('pages.profile.billing.fields.amount'),
                    width: 140,
                    minWidth: 140,
                    maxWidth: 180,
                    isSortable: true,
                },
                {
                    id: Accessors.operationDate,
                    header: t('pages.profile.billing.fields.date'),
                    width: 200,
                    minWidth: 190,
                    maxWidth: 240,
                    filter: <DateRange />,
                    isSortable: true,
                },
                {
                    id: Accessors.transactionType,
                    header: t('pages.profile.billing.fields.type'),
                    filter: (
                        <Select
                            isClearable
                            options={mapArrayToI18nOptions(
                                BILLING_TYPES.map((item) => ({key: item, value: item})),
                                'dictionaries.billingStatus',
                            )}
                        />
                    ),
                    width: 180,
                    minWidth: 170,
                    maxWidth: 220,
                },
                {
                    id: Accessors.paymentStatus,
                    header: t('pages.profile.billing.invoiceGrid.paymentStatus'),
                    width: 180,
                    minWidth: 170,
                    maxWidth: 220,
                    isSortable: true,
                    filter: (
                        <Select
                            isClearable
                            options={BILLING_PAYMENT_STATUSES.map((value) => ({
                                value,
                                label: t('dictionaries.billingPaymentStatus.' + value),
                            }))}
                        />
                    ),
                },
                {
                    id: Accessors.action,
                    header: t('pages.profile.billing.fields.action'),
                    width: 90,
                    minWidth: 90,
                    maxWidth: 110,
                },
            ].filter(
                (column) =>
                    (!access.canViewAllTransactions ||
                        ![Accessors.action, Accessors.paymentStatus].includes(column.id)) &&
                    (column.id !== Accessors.action || access.canRequestInvoice),
            );
        }
        return [
            {
                id: InvoicesAccessors.irn,
                header: t('pages.profile.billing.invoiceGrid.irn'),
                width: 260,
                isSortable: true,
            },
            {
                id: InvoicesAccessors.tin,
                header: t('pages.profile.billing.invoiceGrid.tin'),
                isSortable: true,
            },
            {
                id: InvoicesAccessors.issueDate,
                header: t('pages.profile.billing.invoiceGrid.issuedDate'),
                width: 170,
                isSortable: true,
                filter: <DatePicker />,
            },
            {
                id: InvoicesAccessors.totalInvoicedQuantity,
                header: t('pages.profile.billing.invoiceGrid.invoiceQuantity'),
                width: 170,
                isSortable: false,
            },
            {
                id: InvoicesAccessors.payableAmount,
                header: t('pages.profile.billing.invoiceGrid.payableAmount'),
                width: 175,
                isSortable: true,
            },
            {
                id: InvoicesAccessors.status,
                header: t('pages.profile.billing.invoiceGrid.paymentStatus'),
                width: 205,
                isSortable: false,
                filter: (
                    <Select
                        isClearable
                        options={mapArrayToI18nOptions(
                            BILLING_INVOICE_STATUS.map((item) => ({key: item, value: item})),
                            'dictionaries.billingInvoiceStatus',
                        )}
                    />
                ),
            },
        ];
    }, [t, path, access]);
};
